import React, { useEffect, useState } from 'react';
import { Download, X, Sparkles } from 'lucide-react';
import { paxGetVersionInfo } from '../utils/paxBridge';

/**
 * In-App APK Updater Banner.
 *
 * Renders ONLY when running inside the FlavorFeed PAX APK (i.e. when
 * `paxGetVersionInfo().isNative === true`) AND the latest APK published
 * to `/api/pos/android-app/latest` has a higher version than the one
 * currently installed.
 *
 * UX (matches Shopify / Square in-app updaters):
 *   • Sticky bottom banner — never blocks the POS workspace.
 *   • "Update now" button downloads the .apk; Android's package
 *     installer takes over and the cashier confirms in 2 taps. All
 *     POS data persists because the APK is signed with the same
 *     flavorfeed-release.jks → in-place upgrade.
 *   • "Not now" dismisses for THIS version only, persisted to
 *     localStorage so we don't nag the cashier on every page load
 *     until the next release lands.
 *   • Polls /android-app/latest every 10 minutes — the cashier never
 *     has to manually refresh to discover a new release.
 */
const cmpVersion = (a, b) => {
  // "1.0.15" vs "1.0.9" — split-and-compare each segment as int.
  const pa = String(a || '0').split('.').map((x) => parseInt(x, 10) || 0);
  const pb = String(b || '0').split('.').map((x) => parseInt(x, 10) || 0);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const d = (pa[i] || 0) - (pb[i] || 0);
    if (d !== 0) return d;
  }
  return 0;
};

const DISMISS_KEY = 'apk_update_dismissed_for_version';
const POLL_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes

const ApkUpdaterBanner = () => {
  const [installed, setInstalled] = useState(null);   // { versionName }
  const [latest, setLatest] = useState(null);          // { version, download_url, ... }
  const [dismissed, setDismissed] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Detect native APK + installed version ONCE.
  useEffect(() => {
    const info = paxGetVersionInfo();
    if (info && info.isNative) setInstalled(info);
  }, []);

  // Poll the latest published APK metadata.
  useEffect(() => {
    if (!installed) return;
    let cancelled = false;

    const fetchLatest = async () => {
      try {
        const r = await fetch(`/api/pos/android-app/latest?_=${Date.now()}`, { cache: 'no-store' });
        const data = await r.json();
        if (cancelled) return;
        if (data?.available && data?.version) setLatest(data);
      } catch (e) {
        // offline-tolerant — silently skip and try again on next interval
      }
    };

    fetchLatest();
    const id = setInterval(fetchLatest, POLL_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [installed]);

  // Re-check the dismiss key whenever the latest version changes — a new
  // release wipes the dismissal of the previous one.
  useEffect(() => {
    if (!latest?.version) { setDismissed(false); return; }
    try {
      const d = localStorage.getItem(DISMISS_KEY);
      setDismissed(d === latest.version);
    } catch { setDismissed(false); }
  }, [latest?.version]);

  if (!installed || !latest) return null;
  if (cmpVersion(latest.version, installed.versionName) <= 0) return null;
  if (dismissed) return null;

  const handleUpdate = async () => {
    if (!latest?.download_url) return;
    setDownloading(true);
    try {
      // Trigger the .apk download — Android's WebView intercepts the
      // .apk MIME type and hands the file to the system package
      // installer, which prompts the cashier to confirm.
      const a = document.createElement('a');
      a.href = latest.download_url;
      a.download = latest.filename || 'flavorfeed-pos.apk';
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } finally {
      // Re-enable the button after a short delay in case the install
      // dialog is dismissed and the cashier needs to re-try.
      setTimeout(() => setDownloading(false), 4000);
    }
  };

  const handleDismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, latest.version); } catch { /* ignore */ }
    setDismissed(true);
  };

  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999] w-[95vw] max-w-xl"
      data-testid="apk-updater-banner"
    >
      <div className="rounded-xl border border-fuchsia-500/50 bg-gradient-to-r from-fuchsia-950/95 to-purple-950/95 backdrop-blur shadow-2xl shadow-fuchsia-500/30 p-3 flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-fuchsia-500/20 border border-fuchsia-400/40 flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-5 h-5 text-fuchsia-300" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white font-semibold text-sm">
            FlavorFeed v{latest.version} is available
          </p>
          <p className="text-fuchsia-200/70 text-[11px] truncate">
            You&apos;re on v{installed.versionName} · 1-tap upgrade, your data stays put
          </p>
        </div>
        <button
          onClick={handleUpdate}
          disabled={downloading}
          className="px-4 py-2 bg-fuchsia-500 hover:bg-fuchsia-400 disabled:opacity-60 text-white text-sm font-bold rounded-lg flex items-center gap-1.5 transition-colors flex-shrink-0"
          data-testid="apk-update-now-btn"
        >
          <Download className="w-4 h-4" />
          {downloading ? 'Downloading…' : 'Update now'}
        </button>
        <button
          onClick={handleDismiss}
          className="w-8 h-8 rounded-lg text-fuchsia-300/60 hover:text-white hover:bg-white/10 flex items-center justify-center flex-shrink-0"
          aria-label="Dismiss update notification"
          data-testid="apk-update-dismiss-btn"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default ApkUpdaterBanner;
